import { ArrowUpRight, Instagram, Linkedin, Youtube } from "lucide-react";

import blogBriefingBrand from "@/assets/blog/blog-briefing-brand.jpg";
import blogChefConversation from "@/assets/blog/blog-chef-conversation.jpg";
import blogGalleyHandoff from "@/assets/blog/blog-galley-handoff.jpg";

const SOCIALS = [
  {
    name: "LinkedIn",
    href: "https://www.linkedin.com/company/kryajet",
    handle: "@kryajet",
    blurb: "Product news and industry conversations",
    icon: Linkedin,
    imageSrc: blogBriefingBrand,
    imageAlt: "Kryajet on LinkedIn — industry briefings and product updates",
  },
  {
    name: "Instagram",
    href: "https://www.instagram.com/kryajet/",
    handle: "@kryajet",
    blurb: "Cabin tips, crew stories, The Cabin Club",
    icon: Instagram,
    imageSrc: blogGalleyHandoff,
    imageAlt: "Kryajet on Instagram — cabin crew and catering moments",
  },
  {
    name: "YouTube",
    href: "https://www.youtube.com/@kryajet",
    handle: "@kryajet",
    blurb: "The Kryajet Podcast — aviation catering talk",
    icon: Youtube,
    imageSrc: blogChefConversation,
    imageAlt: "Kryajet on YouTube — podcast and chef conversations",
  },
] as const;

/**
 * Landing social strip — image tiles that open each network in a new tab.
 */
export function MarketingSocialSection() {
  return (
    <section
      id="social"
      className="relative overflow-hidden border-y border-border/40 bg-foreground text-background"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "radial-gradient(ellipse 80% 50% at 20% 0%, hsl(var(--accent)) 0%, transparent 55%)",
        }}
        aria-hidden
      />
      <div className="container relative mx-auto max-w-6xl px-4 py-16 sm:px-6 md:py-20 lg:py-24">
        <div className="max-w-xl" data-reveal-section>
          <p
            data-reveal-item
            className="text-xs font-semibold uppercase tracking-[0.14em] text-background/55"
          >
            Community
          </p>
          <h2
            data-reveal-item
            className="marketing-display mt-3 text-balance text-3xl font-semibold tracking-tight text-background md:text-4xl lg:text-[2.75rem] lg:leading-[1.1]"
          >
            Kryajet on social
          </h2>
          <p data-reveal-item className="mt-4 text-[15px] leading-relaxed text-background/70 md:text-base">
            Follow the platform, the cabin community, and the conversations shaping private aviation
            catering.
          </p>
        </div>

        <ul
          className="mt-10 grid gap-4 sm:grid-cols-2 sm:gap-5 lg:mt-12 lg:grid-cols-3 lg:gap-6"
          data-reveal-section
        >
          {SOCIALS.map(({ name, href, handle, blurb, icon: Icon, imageSrc, imageAlt }) => (
            <li key={name} data-reveal-item>
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="group relative flex h-full flex-col overflow-hidden rounded-2xl border border-background/15 bg-background/[0.04] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background/40 focus-visible:ring-offset-2 focus-visible:ring-offset-foreground"
              >
                <div className="relative aspect-[16/11] overflow-hidden bg-background/10">
                  <img
                    src={imageSrc}
                    alt={imageAlt}
                    className="h-full w-full object-cover opacity-90 transition-[transform,opacity] duration-300 ease-[var(--ease-out)] motion-safe:group-hover:scale-[1.03] group-hover:opacity-100"
                    loading="lazy"
                    decoding="async"
                  />
                  <div
                    className="pointer-events-none absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/25 to-transparent"
                    aria-hidden
                  />
                  <span className="absolute left-3 top-3 flex h-10 w-10 items-center justify-center rounded-full bg-background/90 text-foreground shadow-sm backdrop-blur-sm transition-transform duration-200 ease-[var(--ease-out)] motion-safe:group-hover:scale-[0.97] motion-safe:group-active:scale-[0.95]">
                    <Icon className="h-5 w-5" strokeWidth={1.75} aria-hidden />
                  </span>
                  <ArrowUpRight
                    className="absolute right-3 top-3 h-5 w-5 text-background/80 transition-transform duration-200 ease-[var(--ease-out)] motion-safe:group-hover:translate-x-0.5 motion-safe:group-hover:-translate-y-0.5"
                    aria-hidden
                  />
                </div>
                <span className="flex flex-1 flex-col px-4 py-4 md:px-5 md:py-5">
                  <span className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="text-lg font-semibold tracking-tight text-background md:text-xl">
                      {name}
                    </span>
                    <span className="text-sm text-background/45">{handle}</span>
                  </span>
                  <span className="mt-1 text-sm leading-snug text-background/60 md:text-[15px]">
                    {blurb}
                  </span>
                </span>
                <span className="sr-only">Open {name} (opens in a new tab)</span>
              </a>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
